
import { supabase } from '@/integrations/supabase/client';

/**
 * Delete an entire assessment and all associated data
 * 
 * @param assessmentId - The ID of the assessment to delete
 * @returns Boolean indicating success
 */
export async function deleteAssessment(assessmentId: string): Promise<boolean> {
  try {
    // First, delete all responses associated with this assessment
    const { error: responsesError } = await supabase
      .from('responses')
      .delete()
      .eq('assessment_id', assessmentId);

    if (responsesError) {
      console.error('Error deleting assessment responses:', responsesError);
      return false;
    }

    // Get all categories for this assessment
    const { data: categories, error: categoriesError } = await supabase
      .from('categories')
      .select('id')
      .eq('assessment_id', assessmentId);

    if (categoriesError) {
      console.error('Error fetching assessment categories:', categoriesError);
      return false;
    }

    // Delete all categories (which will cascade delete questions and answers)
    if (categories && categories.length > 0) {
      for (const category of categories) {
        const success = await deleteCategory(assessmentId, category.id);
        if (!success) {
          return false;
        }
      }
    }

    // Finally, delete the assessment itself
    const { error: deleteAssessmentError } = await supabase
      .from('assessments')
      .delete()
      .eq('id', assessmentId);

    if (deleteAssessmentError) {
      console.error('Error deleting assessment:', deleteAssessmentError);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Unexpected error in deleteAssessment:', error);
    return false;
  }
}

/**
 * Delete a category and its associated questions and answers
 * 
 * @param assessmentId - The ID of the assessment
 * @param categoryId - The ID of the category to delete
 * @returns Boolean indicating success
 */
export async function deleteCategory(assessmentId: string, categoryId: string): Promise<boolean> {
  try {
    // Get all questions for this category
    const { data: questions, error: questionsError } = await supabase
      .from('questions')
      .select('id')
      .eq('category_id', categoryId);

    if (questionsError) {
      console.error('Error fetching category questions:', questionsError);
      return false;
    }

    // If there are questions, delete their answers first
    if (questions && questions.length > 0) {
      const questionIds = questions.map(q => q.id);
      
      // Delete answers for these questions
      const { error: answersError } = await supabase
        .from('answers')
        .delete()
        .in('question_id', questionIds);

      if (answersError) {
        console.error('Error deleting question answers:', answersError);
        return false;
      }

      // Delete the questions
      const { error: deleteQuestionsError } = await supabase
        .from('questions')
        .delete()
        .eq('category_id', categoryId);

      if (deleteQuestionsError) {
        console.error('Error deleting category questions:', deleteQuestionsError);
        return false;
      }
    }

    // Finally, delete the category itself
    const { error: deleteCategoryError } = await supabase
      .from('categories')
      .delete()
      .eq('id', categoryId)
      .eq('assessment_id', assessmentId);

    if (deleteCategoryError) {
      console.error('Error deleting category:', deleteCategoryError);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Unexpected error in deleteCategory:', error);
    return false;
  }
}
